import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type EstudoAutosaveStatus = "saved" | "unsaved" | "saving" | "error";

export type EstudoAutosaveController = {
  hasPending: () => boolean;
  flush: () => Promise<boolean>;
};

type EstudoAutosaveContextValue = {
  status: EstudoAutosaveStatus;
  successCount: number;
  errorToastCount: number;
  hasPendingChanges: boolean;
  register: (controller: EstudoAutosaveController | null) => void;
  publish: (patch: {
    status?: EstudoAutosaveStatus;
    hasPendingChanges?: boolean;
  }) => void;
  notifySuccess: () => void;
  notifyErrorToast: () => void;
  hasPending: () => boolean;
  flush: () => Promise<boolean>;
};

const EstudoAutosaveContext = createContext<EstudoAutosaveContextValue | null>(
  null
);

const fallback: EstudoAutosaveContextValue = {
  status: "saved",
  successCount: 0,
  errorToastCount: 0,
  hasPendingChanges: false,
  register: () => {},
  publish: () => {},
  notifySuccess: () => {},
  notifyErrorToast: () => {},
  hasPending: () => false,
  flush: async () => true,
};

export function EstudoAutosaveProvider({ children }: { children: ReactNode }) {
  const controllerRef = useRef<EstudoAutosaveController | null>(null);
  const [status, setStatus] = useState<EstudoAutosaveStatus>("saved");
  const [successCount, setSuccessCount] = useState(0);
  const [errorToastCount, setErrorToastCount] = useState(0);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const register = useCallback(
    (controller: EstudoAutosaveController | null) => {
      controllerRef.current = controller;
    },
    []
  );

  const publish = useCallback(
    (patch: { status?: EstudoAutosaveStatus; hasPendingChanges?: boolean }) => {
      if (patch.status) {
        setStatus((current) =>
          current === patch.status ? current : patch.status!
        );
      }
      if (patch.hasPendingChanges !== undefined) {
        setHasPendingChanges((current) =>
          current === patch.hasPendingChanges
            ? current
            : patch.hasPendingChanges!
        );
      }
    },
    []
  );

  const notifySuccess = useCallback(() => {
    setSuccessCount((count) => count + 1);
  }, []);

  const notifyErrorToast = useCallback(() => {
    setErrorToastCount((count) => count + 1);
  }, []);

  const hasPending = useCallback(
    () => controllerRef.current?.hasPending() ?? false,
    []
  );

  const flush = useCallback(
    () => controllerRef.current?.flush() ?? Promise.resolve(true),
    []
  );

  const value = useMemo(
    () => ({
      status,
      successCount,
      errorToastCount,
      hasPendingChanges,
      register,
      publish,
      notifySuccess,
      notifyErrorToast,
      hasPending,
      flush,
    }),
    [
      status,
      successCount,
      errorToastCount,
      hasPendingChanges,
      register,
      publish,
      notifySuccess,
      notifyErrorToast,
      hasPending,
      flush,
    ]
  );

  return (
    <EstudoAutosaveContext.Provider value={value}>
      {children}
    </EstudoAutosaveContext.Provider>
  );
}

export function useEstudoAutosaveContext(): EstudoAutosaveContextValue {
  return useContext(EstudoAutosaveContext) ?? fallback;
}
